/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * What every Owners block on a best-list page shares (contract #217, 2026-10-18): the reader's own
 * votes on all the picks' reviews — ONE batched `helpful/me` read per page (`useHelpfulMine` over
 * `guideOwners.noteIds`), as `CommunityFeed` does for its rows — the page's one memory of vote
 * answers (the same review shows in the roster row AND in the panel or the sheet: one memory keeps
 * both copies on the same vote), and the page's build time, which the rows' ages are printed
 * against. Mounted by both best-list templates around their lab surfaces, so the side panel
 * (`LabCompareDrawer` → `PickFocusBody`) and the phone sheet read it too.
 */
import { createContext, useContext, useMemo, useState } from 'react'
import { useHelpfulMine } from 'components/community/useHelpfulMine'
import type { GuideOwnersPage } from 'lib/editorial/guidePicks'
import type { CommunityHelpfulAnswer } from 'lib/community/types'

export interface GuideOwnersShared {
  now: number
  helpfulMemory: Map<string, CommunityHelpfulAnswer>
  helpfulMine: ReadonlyMap<string, CommunityHelpfulAnswer>
}

const GuideOwnersContext = createContext<GuideOwnersShared | null>(null)

const NO_IDS: string[] = []

export const GuideOwnersProvider: React.FC<{
  page: GuideOwnersPage | undefined
  children?: React.ReactNode
}> = ({ page, children }) => {
  const [helpfulMemory] = useState(() => new Map<string, CommunityHelpfulAnswer>())
  const helpfulMine = useHelpfulMine(page?.noteIds ?? NO_IDS)
  // A page built before `guideOwners` existed has no build time: no provider value, and each
  // block falls back to its own (`PickOwners`).
  const now = page?.now ?? null
  const value = useMemo(
    () => (now === null ? null : { now, helpfulMemory, helpfulMine }),
    [now, helpfulMemory, helpfulMine]
  )
  return <GuideOwnersContext.Provider value={value}>{children}</GuideOwnersContext.Provider>
}

/** The page's shared Owners state; null outside a best-list template (or on an older page). */
export function useGuideOwners(): GuideOwnersShared | null {
  return useContext(GuideOwnersContext)
}
